import React, {useEffect, useRef, useState} from 'react';
import {Platform, Pressable, ScrollView, View} from 'react-native';
import {useQueryClient} from '@tanstack/react-query';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import {
  useClubDetail,
  useClubPromotion,
  useMarkPromotionSeen,
  useClubActiveEvent,
  useEndEvent,
  useClubActiveTournament,
  useClubActivities,
} from '@/Hooks/ClubsApi/useClubsQueries';
import {useSocket} from '@/Hooks/UseSocket';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import ENV from '../../../environment';
import ChatTab from './components/ChatTab';
import LeaderboardTab from './components/LeaderboardTab';
import InterClubBoard from './components/InterClubBoard';
import MembersTab from './components/MembersTab';
import InviteShare from './components/InviteShare';
import ClubBadge from './components/ClubBadge';
import PromotionModal from './components/PromotionModal';
import ClubPulse, {ClubActivityLauncher} from './components/ClubPulse';
import ScheduleModal from './components/ScheduleModal';
import ClubFunChallengeSheet from './components/ClubFunChallengeSheet';
import ClubCompareModal from './components/ClubCompareModal';
import LeaveClubModal from './components/LeaveClubModal';
import {ClubRoomSkeleton} from './components/ClubRoomSkeletons';
import {BADGES} from './badgeOptions';
import type {PieceKey} from './clubPieces';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import {
  armRoyaleRoomCreatedResolver,
  clearRoyaleRoomCreatedResolver,
} from '@/Hooks/FunChallengesSocket/royaleSocketRouting';
import {
  ACTIVE_FUN_CHALLENGE_KEY,
  useActiveClubFunChallenge,
  useRegisterClubFunChallenge,
} from '@/Hooks/ClubsApi/useClubFunChallenge';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';
import {useLeaveClub} from '@/Hooks/ClubsApi/useClubsMutations';
import type {ClubMemberRow} from '@/Hooks/ClubsApi/types';
import {removeLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';

const TABS = ['chat', 'leaderboard', 'clubs', 'members'] as const;
const TAB_LABELS: Record<(typeof TABS)[number], string> = {
  chat: 'Chat',
  leaderboard: 'Leaderboard',
  clubs: 'Top Clubs',
  members: 'Members',
};
const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(b => b.id === badgeId)?.piece ?? 'pawn') as PieceKey;

function ClubHeaderActions({
  isWide,
  children,
}: {
  isWide: boolean;
  children: React.ReactNode;
}) {
  if (isWide) {
    return (
      <View
        testID="club-header-actions"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 8,
        }}>
        {children}
      </View>
    );
  }

  return (
    <View
      testID="club-header-actions"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 0,
        gap: 6,
        marginLeft: 'auto',
      }}>
      {children}
    </View>
  );
}

export default function ClubRoom({navigation, route}: any) {
  const {clubId} = route?.params ?? {};
  const {data, isLoading} = useClubDetail(clubId);
  const [tab, setTab] = useState<(typeof TABS)[number]>('chat');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const leaveClub = useLeaveClub(clubId);
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const isStaff = useLoggedInUserStore(state => !!state.userData?.is_staff);

  const goToClubs = () => navigation.navigate('Clubs');

  // Promotion celebration — shown once when the club climbed a division.
  const {data: promoData} = useClubPromotion(clubId);
  const markSeen = useMarkPromotionSeen(clubId);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const promotion = promoData?.promotion ?? null;
  const dismissPromotion = () => {
    setPromoDismissed(true);
    markSeen.mutate();
  };

  // Puzzle Storm event — pinned at the top of the room when active.
  const {data: eventData} = useClubActiveEvent(clubId);
  const endEvent = useEndEvent(clubId);
  const activeEvent = eventData?.event ?? null;

  // Upcoming tournament highlight — shown below the event bar when active.
  const {data: tData} = useClubActiveTournament(clubId);
  const {data: activitiesData} = useClubActivities(clubId);
  const eventServiceTournaments =
    tData?.tournaments ?? (tData?.tournament ? [tData.tournament] : []);
  // The activity callback is the durable source for a newly scheduled club
  // tournament. Merge it with ccm's upcoming list so Club Pulse appears even
  // when the event-service index has not refreshed yet.
  const activityTournaments = (activitiesData?.activities ?? []).filter(
    activity => activity?.activity_type === 'tournament',
  );
  const upcomingTournaments = Array.from(
    new Map(
      [...eventServiceTournaments, ...activityTournaments].map(tournament => [
        String(tournament.id),
        tournament,
      ]),
    ).values(),
  );

  // Live-refresh the tournament card both when one is created and at its reminder.
  const socket = useSocket();
  const qc = useQueryClient();
  useEffect(() => {
    if (!socket) return;
    const parseEvent = (eventData: any) => {
      try {
        const first = JSON.parse(eventData?.message);
        return typeof first === 'string' ? JSON.parse(first) : first;
      } catch {
        return null;
      }
    };
    const matchesClub = (eventData: any) => {
      const p = parseEvent(eventData);
      return p && String(p.club_id) === String(clubId);
    };
    const onTournamentSoon = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(['clubs', 'active-tournament', clubId]);
      }
    };
    socket.registerMessageHandler?.(
      'club_tournament_soon',
      onTournamentSoon,
      `ClubTrn:${clubId}`,
    );
    socket.registerMessageHandler?.(
      'club_tournament_created',
      onTournamentSoon,
      `ClubTrnCreated:${clubId}`,
    );
    const onFunChallenge = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(ACTIVE_FUN_CHALLENGE_KEY(clubId));
      }
    };
    socket.registerMessageHandler?.(
      'club_fun_challenge_update',
      onFunChallenge,
      `ClubFun:${clubId}`,
    );
    const onDetail = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(['clubs', 'detail', clubId]);
      }
    };
    const onMembership = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(['clubs', 'detail', clubId]);
        qc.invalidateQueries(['clubs', 'mine']);
        qc.invalidateQueries(['clubs', 'requests', clubId]);
      }
    };
    const onEvent = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(['clubs', 'event', clubId]);
      }
    };
    const onScore = (_s: any, eventData: any) => {
      if (matchesClub(eventData)) {
        qc.invalidateQueries(['clubs', 'leaderboard', clubId]);
        qc.invalidateQueries(['clubs', 'event', clubId]);
        qc.invalidateQueries(['clubs', 'detail', clubId]);
        qc.invalidateQueries(['clubs', 'inter']);
        qc.invalidateQueries(['clubs', 'standings']);
      }
    };
    socket.registerMessageHandler?.(
      'club_detail_update',
      onDetail,
      `ClubDetail:${clubId}`,
    );
    socket.registerMessageHandler?.(
      'club_membership_update',
      onMembership,
      `ClubMembers:${clubId}`,
    );
    socket.registerMessageHandler?.(
      'club_event_update',
      onEvent,
      `ClubEvent:${clubId}`,
    );
    socket.registerMessageHandler?.(
      'club_score_update',
      onScore,
      `ClubScore:${clubId}`,
    );
  }, [socket, clubId, qc]);

  // Schedule modal for tournaments and events
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  // Fun Challenge (Puzzle Royale) — admin creates for the club from here.
  const [funSheetOpen, setFunSheetOpen] = useState(false);
  const [funCreated, setFunCreated] = useState(false);
  const funCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [compareMemberId, setCompareMemberId] = useState<number | null>(null);
  const [funError, setFunError] = useState('');
  const [funSubmitting, setFunSubmitting] = useState(false);
  const {
    createLobby,
    joinLobby,
    ready: royaleReady,
    authed: royaleAuthed,
  } = useRoyaleSocket();
  const activeFunQ = useActiveClubFunChallenge(clubId);
  const registerFun = useRegisterClubFunChallenge(clubId);
  const liveFunChallenge = (activeFunQ as any)?.data?.challenge ?? null;

  // Get creator display name: try to read from the logged-in user store
  const myDisplayName = useLoggedInUserStore(
    state => state.userData?.username || state.userData?.first_name || 'Admin',
  );

  useEffect(
    () => () => {
      if (funCloseTimerRef.current) {
        clearTimeout(funCloseTimerRef.current);
      }
    },
    [],
  );

  const openFunSheet = () => {
    setFunError('');
    setFunCreated(false);
    setFunSheetOpen(true);
  };

  const closeFunSheet = () => {
    if (funCloseTimerRef.current) {
      clearTimeout(funCloseTimerRef.current);
      funCloseTimerRef.current = null;
    }
    setFunSheetOpen(false);
    setFunCreated(false);
    setFunError('');
    setFunSubmitting(false);
    clearRoyaleRoomCreatedResolver();
  };

  const handleCreateFunChallenge = (settings: RoyaleSettingsValue) => {
    setFunError('');

    // The socket check lives HERE, not on the button: the lobby is born from a
    // fun_room_create send, and sending before auth completes gets the server's
    // "auth failed" rejection. Failing here tells the admin what to do instead
    // of silently greying the button out on a fresh mount.
    if (!royaleReady || !royaleAuthed) {
      setFunError(
        'Still connecting to the game server — try again in a moment.',
      );
      return;
    }

    setFunSubmitting(true);

    // Arm BEFORE sending: on a 2nd+ create the store can still hold the PREVIOUS
    // room, so the registration must use the fun_room_created ACK's id, never a
    // store read. See royaleSocketRouting's resolver comment.
    armRoyaleRoomCreatedResolver(async ({roomId, shareCode, expiresAt}) => {
      try {
        await registerFun.mutateAsync({
          room_id: roomId,
          share_code: shareCode,
          settings: settings as any,
          expires_at: expiresAt,
        });
        setFunCreated(true);
        funCloseTimerRef.current = setTimeout(() => {
          setFunSheetOpen(false);
          setFunCreated(false);
          funCloseTimerRef.current = null;
        }, 1800);
      } catch (e: any) {
        setFunError(
          e?.response?.data?.message ||
            e?.message ||
            'Could not start the challenge',
        );
      } finally {
        setFunSubmitting(false);
      }
    });

    try {
      createLobby({
        // Creation announces the race through notifications. Nobody is
        // auto-joined; members enter from the live header action.
        hostIsPlayer: false,
        settings: settings as any,
        creatorName: myDisplayName,
      });
    } catch (e: any) {
      clearRoyaleRoomCreatedResolver();
      setFunSubmitting(false);
      setFunError('Could not reach the game server');
    }
  };

  const handleJoinFunChallenge = () => {
    if (!liveFunChallenge?.share_code) return;
    joinLobby(liveFunChallenge.share_code, {username: myDisplayName});
    navigation.navigate('PuzzleRoyaleBattle');
  };

  // Non-active clubs (disabled/banned/pending_review/archived) must not open the
  // room — redirect to the profile, which shows the status banner + raise-a-ticket
  // note. Effect (not render-time) so navigation happens as a side effect.
  const clubStatus = data?.club?.status;
  const isClubActive = (clubStatus ?? 'active') === 'active';
  useEffect(() => {
    if (data && !isClubActive) {
      navigation.navigate('ClubProfile', {clubId});
    }
  }, [data, isClubActive, clubId, navigation]);

  if (isLoading || !data) {
    return <ClubRoomSkeleton isWide={isWide} />;
  }
  if (!isClubActive) {
    // While the redirect effect runs, don't render the room.
    return <CCLoader loading={true} />;
  }
  const club = data.club;
  const isMember = !!club.my_role;
  const isAdmin = club.my_role === 'admin';
  const clubMembers = club.members ?? [];
  const replacementCandidates = clubMembers.filter(
    member => member.role === 'member',
  );
  const isSoleAdmin =
    isAdmin &&
    clubMembers.filter(member => member.role === 'admin').length === 1;
  const requiresReplacement = isSoleAdmin && club.member_count > 1;
  const canManage = isAdmin || isStaff;
  const memberLabel = `${club.member_count} member${
    club.member_count !== 1 ? 's' : ''
  }`;
  const handleChallengeMember = async (member: ClubMemberRow) => {
    await Promise.all([
      removeLocalStorage('challengePacket'),
      removeLocalStorage('club_challenge_id'),
      setLocalStorage('club_opp_id', String(member.user_id)),
      setLocalStorage('club_opp_name', String(member.name || member.username)),
      setLocalStorage('club_challenge_tc', '5+0'),
      setLocalStorage('club_challenge_role', 'creator'),
      setLocalStorage('club_return_id', String(clubId)),
    ]);
    navigation.navigate('PlayChess');
  };
  const renderInviteButton = () => (
    <Pressable
      testID={isWide ? 'club-invite-btn-wide' : 'club-invite-btn'}
      onPress={() => setInviteOpen(true)}
      style={{
        flexShrink: 0,
        backgroundColor: '#3DAB9E',
        borderRadius: isWide ? 22 : 20,
        paddingHorizontal: isWide ? 18 : 10,
        paddingVertical: isWide ? 10 : 7,
        marginLeft: 0,
      }}>
      <CCText
        style={{
          color: '#fff',
          fontWeight: '600',
          fontSize: isWide ? 14 : 11,
        }}>
        Invite
      </CCText>
    </Pressable>
  );
  const renderLeaveButton = () => (
    <Pressable
      testID="club-room-leave-btn"
      onPress={() => setLeaveOpen(true)}
      style={{
        flexShrink: 0,
        backgroundColor: '#FFF7F5',
        borderWidth: 1,
        borderColor: '#D66A61',
        borderRadius: isWide ? 22 : 20,
        paddingHorizontal: isWide ? 16 : 10,
        paddingVertical: isWide ? 10 : 7,
      }}>
      <CCText
        style={{
          color: '#B54840',
          fontWeight: '600',
          fontSize: isWide ? 14 : 11,
        }}>
        Leave
      </CCText>
    </Pressable>
  );
  const confirmLeave = async (replacementAdminId?: number) => {
    setLeaveError('');
    try {
      await leaveClub.mutateAsync(replacementAdminId);
      setLeaveOpen(false);
      goToClubs();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      setLeaveError(
        code === 'replacement_admin_required'
          ? 'Choose the next admin before leaving.'
          : code === 'invalid_replacement_admin'
          ? 'Choose a current club member.'
          : 'Could not leave the club. Please try again.',
      );
    }
  };

  return (
    <View
      testID="club-room-root"
      style={[
        {
          flex: 1,
          width: '100%',
          minWidth: 0,
          alignSelf: 'stretch',
          backgroundColor: '#FAF6EB',
        },
        // ClubRoom is hosted in a fixed-height View by Application, so the room
        // can own the available viewport and keep the composer at the bottom.
        // Scrolling stays inside the messages and right rail.
        isWide && Platform.OS === 'web'
          ? ({
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            } as any)
          : null,
      ]}>
      {promotion && !promoDismissed ? (
        <PromotionModal promotion={promotion} onDismiss={dismissPromotion} />
      ) : null}
      {/* Header bar */}
      <View
        testID="club-room-header"
        style={{
          flexDirection: 'row',
          flexWrap: isWide ? 'nowrap' : 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: isWide ? 0 : 4,
          rowGap: isWide ? 0 : 6,
          paddingHorizontal: isWide ? 28 : 10,
          paddingTop: isWide ? 20 : 5,
          paddingBottom: isWide ? 16 : 6,
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(38,35,34,0.10)',
        }}>
        <View
          testID="club-header-identity"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            flexGrow: 1,
            flexShrink: 1,
            minWidth: isWide ? undefined : 170,
            gap: isWide ? 14 : 0,
          }}>
          {/* Back button — mobile shows a compact chevron; desktop shows a
              "‹ Clubs" pill so the wide layout also has a way back. */}
          {isWide ? (
            <Pressable
              testID="club-room-back"
              onPress={goToClubs}
              style={({pressed}) => [
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: 'rgba(38,35,34,0.20)',
                  backgroundColor: '#fff',
                  marginRight: 4,
                },
                pressed && {opacity: 0.7},
              ]}>
              <CCText style={{fontSize: 16, lineHeight: 16, color: '#4D3F37'}}>
                ‹
              </CCText>
              <CCText
                style={{fontSize: 13.5, fontWeight: '600', color: '#4D3F37'}}>
                Clubs
              </CCText>
            </Pressable>
          ) : (
            <Pressable
              testID="club-room-back"
              onPress={goToClubs}
              style={{
                paddingHorizontal: 4,
                paddingVertical: 6,
                marginRight: 4,
              }}>
              <CCText style={{fontSize: 22, lineHeight: 22, color: '#4D3F37'}}>
                ‹
              </CCText>
            </Pressable>
          )}
          {/* Club badge crest */}
          <View style={{marginRight: isWide ? 0 : 10}}>
            <ClubBadge
              piece={pieceFor(club.badge_id)}
              color={club.background_color || '#3DAB9E'}
              size={isWide ? 48 : 34}
              radius={isWide ? 13 : 10}
            />
          </View>
          <View style={{flex: 1, minWidth: 0}}>
            {isWide ? (
              <CCText
                fontWeight="thunder"
                style={{fontSize: 24, color: '#262322'}}>
                {club.name}
              </CCText>
            ) : (
              <CCText
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: '#262322',
                  lineHeight: 18,
                }}>
                {club.name}
              </CCText>
            )}
            <CCText
              style={{
                fontSize: isWide ? 12.5 : 11.5,
                color: '#888280',
                marginTop: 2,
              }}>
              {memberLabel}
            </CCText>
          </View>
        </View>
        <ClubHeaderActions isWide={isWide}>
          {renderInviteButton()}
          {isMember ? renderLeaveButton() : null}
          {canManage ? (
            <Pressable
              testID="club-manage-btn"
              style={{
                backgroundColor: '#fff',
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.20)',
                paddingHorizontal: isWide ? 16 : 12,
                paddingVertical: isWide ? 10 : 8,
                borderRadius: isWide ? 22 : 20,
                marginLeft: 0,
              }}
              onPress={() => navigation.navigate('ClubAdmin', {clubId})}>
              <CCText
                style={{
                  color: '#4D3F37',
                  fontWeight: '600',
                  fontSize: isWide ? 14 : 12.5,
                }}>
                Manage
              </CCText>
            </Pressable>
          ) : null}
        </ClubHeaderActions>
      </View>

      {club.announcement?.text ? (
        <View
          testID="club-pinned-announcement"
          style={{
            minHeight: isWide ? 42 : 32,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            marginHorizontal: isWide ? 28 : 14,
            marginTop: isWide ? 10 : 5,
            paddingHorizontal: isWide ? 13 : 10,
            paddingVertical: isWide ? 7 : 5,
            borderRadius: isWide ? 11 : 10,
            borderWidth: 1,
            borderColor: 'rgba(224,176,45,0.28)',
            backgroundColor: '#FFF3CF',
          }}>
          <CCText style={{fontSize: isWide ? 14 : 12}}>📌</CCText>
          <CCText
            style={{
              color: '#A77D17',
              fontSize: isWide ? 9 : 8.5,
              fontWeight: '800',
              letterSpacing: 0.4,
            }}>
            PINNED
          </CCText>
          <CCText
            numberOfLines={1}
            style={{
              flex: 1,
              color: '#6F5428',
              fontSize: isWide ? 12.5 : 11,
            }}>
            {club.announcement.text}
          </CCText>
        </View>
      ) : null}

      {!isWide ? (
        <>
          <ClubActivityLauncher
            mobile
            isAdmin={isAdmin}
            puzzleRoyalLive={!!liveFunChallenge}
            onScheduleTournament={() => setScheduleModalOpen(true)}
            onPuzzleRoyal={
              liveFunChallenge ? handleJoinFunChallenge : openFunSheet
            }
          />
          <ClubPulse
            variant="mobile"
            event={activeEvent}
            tournaments={upcomingTournaments}
            isAdmin={isAdmin}
            onEndEvent={() =>
              activeEvent ? endEvent.mutate(activeEvent.id) : undefined
            }
            onTournamentPress={tournament =>
              navigation.navigate('CCTournaments', {
                tournamentId: tournament.id,
              })
            }
          />
        </>
      ) : null}
      {/* Invite bottom-sheet (opened from the Invite button) */}
      <InviteShare
        clubId={clubId}
        clubName={club.name}
        visibility={club.visibility}
        joinPolicy={club.join_policy}
        minRating={club.min_rating}
        visible={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      <LeaveClubModal
        visible={leaveOpen}
        clubName={club.name}
        members={replacementCandidates}
        requiresReplacement={requiresReplacement}
        isLastMember={club.member_count <= 1}
        submitting={leaveClub.isLoading}
        error={leaveError}
        onClose={() => {
          setLeaveOpen(false);
          setLeaveError('');
        }}
        onConfirm={confirmLeave}
      />

      {/* Schedule modal for tournaments and puzzle storm events */}
      <ScheduleModal
        clubId={clubId}
        clubName={club.name}
        visible={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
      />

      <ClubFunChallengeSheet
        visible={funSheetOpen}
        clubName={club.name}
        submitting={funSubmitting}
        created={funCreated}
        error={funError}
        onClose={closeFunSheet}
        onCreate={handleCreateFunChallenge}
      />

      {compareMemberId != null ? (
        <ClubCompareModal
          visible
          clubId={clubId}
          rightId={compareMemberId}
          onClose={() => setCompareMemberId(null)}
        />
      ) : null}

      {isWide ? (
        // ===== Desktop two-pane: chat card + right rail =====
        // The pane fills the remaining viewport height and never overflows; the
        // page itself doesn't scroll. Scrolling lives inside the chat message
        // list and inside each rail card.
        <View
          testID="club-room-desktop-pane"
          style={{
            flex: 1,
            flexDirection: 'row',
            minHeight: 0,
            overflow: 'hidden',
            paddingTop: 16,
            paddingHorizontal: 28,
            paddingBottom: 0,
            gap: 20,
            // Fill all remaining height below the header + optional bars, down to the
            // viewport bottom. The chat card and rail flex to fill this, so they
            // self-adjust to whichever slots (announcement / event bar) are shown.
          }}>
          {/* Chat card — fills full height; its inner FlatList scrolls, composer
              stays pinned to the bottom (design line 869). minHeight:0 lets the
              inner list shrink/scroll instead of stretching the column. */}
          <View
            testID="club-chat-tab"
            style={{
              flex: 2,
              minHeight: 0,
              alignSelf: 'stretch',
              backgroundColor: '#fff',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderWidth: 1,
              borderBottomWidth: 0,
              borderColor: 'rgba(38,35,34,0.10)',
              overflow: 'hidden',
            }}>
            <ChatTab
              clubId={clubId}
              isWide
              isAdmin={isAdmin}
              navigation={navigation}
              tournaments={upcomingTournaments}
            />
          </View>
          {/* Right rail — the WHOLE rail scrolls as one column. The leaderboard card
              sizes to ~10 rows (scrolls inside beyond that); the members card sits
              below it, peeking into view so it's clearly scrollable to. */}
          <ScrollView
            testID="club-room-right-rail"
            style={{flex: 1, minHeight: 0, alignSelf: 'stretch'}}
            contentContainerStyle={{gap: 16, paddingBottom: 8}}
            showsVerticalScrollIndicator={false}>
            <ClubActivityLauncher
              mobile={false}
              isAdmin={isAdmin}
              puzzleRoyalLive={!!liveFunChallenge}
              onScheduleTournament={() => setScheduleModalOpen(true)}
              onPuzzleRoyal={
                liveFunChallenge ? handleJoinFunChallenge : openFunSheet
              }
            />
            <ClubPulse
              variant="rail"
              event={activeEvent}
              tournaments={upcomingTournaments}
              isAdmin={isAdmin}
              onEndEvent={() =>
                activeEvent ? endEvent.mutate(activeEvent.id) : undefined
              }
              onTournamentPress={tournament =>
                navigation.navigate('CCTournaments', {
                  tournamentId: tournament.id,
                })
              }
            />
            {/* Leaderboard card — sizes to content (LeaderboardTab caps + scrolls). */}
            <View
              style={{
                overflow: 'hidden',
              }}>
              <LeaderboardTab clubId={clubId} variant="rail" navigation={navigation} />
            </View>
            {/* Inter-club board card */}
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.10)',
                overflow: 'hidden',
              }}>
              <InterClubBoard scope="public" embedded />
            </View>
            {/* Members card — fixed title, scrollable list inside. */}
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.10)',
                shadowColor: '#4D3F37',
                shadowOffset: {width: 0, height: 1},
                shadowOpacity: 0.05,
                shadowRadius: 2,
                overflow: 'hidden',
              }}>
              <CCText
                style={{
                  fontSize: 13,
                  fontWeight: '700',
                  color: '#262322',
                  marginBottom: 10,
                }}>
                Members · {memberLabel}
              </CCText>
              <MembersTab
                members={clubMembers}
                variant="rail"
                onChallenge={handleChallengeMember}
                onCompare={setCompareMemberId}
                onViewProfile={
                  ENV.PLAYER_PROFILE_ENABLED
                    ? m =>
                        // `push` so a fresh entry is added with the correct
                        // predecessor (see LeaderboardTab).
                        navigation.push
                          ? navigation.push('PlayerProfile', {
                              username: m.username,
                            })
                          : navigation.navigate('PlayerProfile', {
                              username: m.username,
                            })
                    : undefined
                }
              />
            </View>
          </ScrollView>
        </View>
      ) : (
        // ===== Mobile: tab bar + single tab body =====
        <>
          <View
            style={{
              flexDirection: 'row',
              gap: 6,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(38,35,34,0.10)',
              paddingHorizontal: 14,
              paddingTop: 8,
            }}>
            {TABS.map(t => {
              const active = tab === t;
              return (
                <Pressable
                  key={t}
                  testID={`club-tab-${t}`}
                  onPress={() => setTab(t)}
                  style={{
                    paddingBottom: 9,
                    paddingTop: 2,
                    paddingHorizontal: 4,
                    alignItems: 'center',
                    borderBottomWidth: 2.5,
                    borderBottomColor: active ? '#3DAB9E' : 'transparent',
                    marginBottom: -1,
                  }}>
                  <CCText
                    style={{
                      fontWeight: '600',
                      color: active ? '#262322' : '#888280',
                      fontSize: 14,
                    }}>
                    {TAB_LABELS[t]}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
          {tab === 'chat' ? (
            <View testID="club-chat-tab" style={{flex: 1}}>
              <ChatTab
                clubId={clubId}
                isAdmin={isAdmin}
                navigation={navigation}
                tournaments={upcomingTournaments}
              />
            </View>
          ) : tab === 'leaderboard' ? (
            <LeaderboardTab clubId={clubId} navigation={navigation} />
          ) : tab === 'clubs' ? (
            <View
              testID="club-top-clubs-tab"
              style={{
                flex: 1,
                paddingHorizontal: 14,
                paddingTop: 12,
                paddingBottom: 16,
              }}>
              <InterClubBoard scope="public" embedded />
            </View>
          ) : (
            <MembersTab
              members={clubMembers}
              onChallenge={handleChallengeMember}
              onCompare={setCompareMemberId}
              onViewProfile={
                ENV.PLAYER_PROFILE_ENABLED
                  ? m =>
                      // `push` so a fresh entry is added with the correct
                      // predecessor (see LeaderboardTab).
                      navigation.push
                        ? navigation.push('PlayerProfile', {
                            username: m.username,
                          })
                        : navigation.navigate('PlayerProfile', {
                            username: m.username,
                          })
                  : undefined
              }
            />
          )}
        </>
      )}
    </View>
  );
}
